import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectTestId, selectTestStatus } from "@store/tests/testSelectors";
import { selectTestSuiteSessionId } from "@store/testSuite/testSuiteSelectors";
import { selectWorklistContext } from "@store/patients/patientSelectors";
import { TestStatus } from "types/tests/core";
import { completeMpps, getPacsConfig, startMpps } from "@utils/api-communication/RestApiCli";
import { deriveStudyInstanceUid, getDicomUidRoot } from "@utils/dicom/uid";
import { usePacsFeatureEnabled } from "@utils/dicom/pacsFeature";

export default function MppsHost() {
  const pacsEnabled = usePacsFeatureEnabled();
  const testStatus = useSelector(selectTestStatus);
  const testId = useSelector(selectTestId);
  const suiteSessionId = useSelector(selectTestSuiteSessionId);
  const worklistContext = useSelector(selectWorklistContext);
  const location = useLocation();
  const lastStatus = useRef<TestStatus | null>(null);
  const mppsSopInstanceUid = useRef<string | null>(null);

  const isAutoSaveIframe =
    new URLSearchParams(location.search).get("autosave") === "1" && window.parent !== window;

  useEffect(() => {
    const prev = lastStatus.current;
    lastStatus.current = testStatus;
    if (prev === null || isAutoSaveIframe || suiteSessionId) return;
    if (!pacsEnabled) return;

    if (prev !== TestStatus.Started && testStatus === TestStatus.Started) {
      void reportInProgress();
    }
    if (prev !== TestStatus.Completed && testStatus === TestStatus.Completed) {
      void reportCompleted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testStatus, pacsEnabled, isAutoSaveIframe, suiteSessionId]);

  function buildUids(): { studyInstanceUid: string; uidRoot: string } | null {
    try {
      const uidRoot = getDicomUidRoot();
      const studyInstanceUid = worklistContext?.studyInstanceUid || deriveStudyInstanceUid(uidRoot);
      return { studyInstanceUid, uidRoot };
    } catch {
      return null;
    }
  }

  async function reportInProgress() {
    if (!testId || !worklistContext) return;
    let pacsConfig;
    try {
      pacsConfig = await getPacsConfig();
    } catch {
      return;
    }
    if (!pacsConfig.mpps?.enabled) return;
    const uids = buildUids();
    if (!uids) return;
    try {
      const result = await startMpps(testId, {
        uids,
        accessionNumber: worklistContext.accessionNumber,
        scheduledStepId: worklistContext.scheduledStepId,
      });
      mppsSopInstanceUid.current = result.sopInstanceUid;
    } catch {
      mppsSopInstanceUid.current = null;
    }
  }

  async function reportCompleted() {
    const sopInstanceUid = mppsSopInstanceUid.current;
    if (!sopInstanceUid) return;
    mppsSopInstanceUid.current = null;
    const uids = buildUids();
    if (!uids) return;
    try {
      await completeMpps(sopInstanceUid, { uids, status: "COMPLETED" });
    } catch {
      // best effort; RIS status is not critical to the operator's flow
    }
  }

  return null;
}
